import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import {
  Banknote, ArrowLeft, ArrowRight, User, Phone, MapPin, Briefcase,
  Building2, DollarSign, FileText, Shield, Upload, Check, Loader2,
  ChevronRight, AlertCircle, X, Eye, EyeOff, Calculator, Save
} from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STEPS = [
  { id: 'personal', label: 'Personal', icon: User },
  { id: 'contact', label: 'Contact', icon: Phone },
  { id: 'address', label: 'Address', icon: MapPin },
  { id: 'employment', label: 'Employment', icon: Briefcase },
  { id: 'bank', label: 'Bank', icon: Building2 },
  { id: 'financial', label: 'Financial', icon: DollarSign },
  { id: 'loan', label: 'Loan', icon: Banknote },
  { id: 'documents', label: 'Documents', icon: FileText },
  { id: 'affordability', label: 'Affordability', icon: Calculator },
  { id: 'consent', label: 'Submit', icon: Shield },
];

const AFFORDABILITY_SECTIONS = [
  { key: 'income', label: 'Income', color: 'emerald', items: [
    { key: 'bonus_income', label: 'Bonus Income' },
    { key: 'other_income', label: 'Other Income' },
    { key: 'primary_income', label: 'Primary Income' },
  ]},
  { key: 'statutory_deductions', label: 'Statutory Deductions', color: 'blue', items: [
    { key: 'paye', label: 'PAYE' },
    { key: 'insurance_ded', label: 'Insurance' },
    { key: 'uif', label: 'UIF' },
  ]},
  { key: 'expenses', label: 'Expenses', color: 'amber', items: [
    { key: 'medical', label: 'Medical' },
    { key: 'transport', label: 'Transport' },
    { key: 'utilities', label: 'Utilities' },
    { key: 'accommodation', label: 'Accommodation' },
    { key: 'education', label: 'Education' },
    { key: 'food', label: 'Food' },
    { key: 'maintenance', label: 'Maintenance' },
  ]},
  { key: 'debt_obligations', label: 'Debt Obligations', color: 'red', items: [
    { key: 'credit_card_payment', label: 'Credit Card Payment' },
    { key: 'current_loans', label: 'Current Loans' },
    { key: 'loan_repayments', label: 'Loan Repayments' },
  ]},
];

const getMonthLabels = () => {
  const now = new Date();
  return [3, 2, 1].map(i => {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    return d.toLocaleString('en-ZA', { month: 'long' });
  });
};

const initAffordability = () => {
  const data = {};
  AFFORDABILITY_SECTIONS.forEach(sec => {
    data[sec.key] = {};
    sec.items.forEach(item => {
      data[sec.key][item.key] = { month1: '', month2: '', month3: '', notes: '', final: '' };
    });
  });
  return data;
};

const SA_PROVINCES = [
  'Eastern Cape', 'Free State', 'Gauteng', 'KwaZulu-Natal',
  'Limpopo', 'Mpumalanga', 'North West', 'Northern Cape', 'Western Cape'
];

const SA_BANKS = [
  'ABSA', 'Capitec', 'FNB', 'Nedbank', 'Standard Bank',
  'African Bank', 'TymeBank', 'Discovery Bank', 'Investec', 'Other'
];

const BANK_BRANCH_CODES = {
  'ABSA': '632005',
  'Capitec': '470010',
  'FNB': '250655',
  'Nedbank': '198765',
  'Standard Bank': '051001',
  'African Bank': '430000',
  'TymeBank': '678910',
  'Discovery Bank': '679000',
  'Investec': '580105',
};

export default function LoanApplication() {
  const navigate = useNavigate();
  const { user, token, getAuthHeader, register, login } = useAuth();
  const [step, setStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadingField, setUploadingField] = useState(null);
  const [existingApp, setExistingApp] = useState(null);
  const [showInlineAuth, setShowInlineAuth] = useState(false);
  const [authMode, setAuthMode] = useState('register'); // 'register' or 'login'
  const [authLoading, setAuthLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [regForm, setRegForm] = useState({ password: '', confirmPassword: '', loginEmail: '', loginPassword: '' });
  const [savingDraft, setSavingDraft] = useState(false);
  const [hasDraft, setHasDraft] = useState(false);

  const [form, setForm] = useState({
    title: '', first_name: '', last_name: '', id_number: '',
    date_of_birth: '', gender: '', marital_status: '', dependants: 0,
    cell_number: '', email: '', alt_number: '',
    street_address: '', suburb: '', city: '', province: '',
    postal_code: '', residential_status: '', years_at_address: '',
    employment_status: 'employed', employer_name: '', employer_phone: '',
    employer_email: '', employer_contact_person: '', employer_address: '',
    employer_suburb: '', employer_city: '', employer_postal_code: '',
    occupation: '', employment_date: '', salary_date: '',
    bank_name: '', account_type: 'savings', account_number: '',
    branch_code: '', account_holder: '',
    gross_income: '', net_income: '', total_expenses: '',
    total_deductions: '', existing_loans: '',
    loan_amount: 2000, loan_purpose: '', repayment_period: 30,
    debit_order_date: '', debit_order_account: '',
    id_document_file_id: null, payslip_file_id: null,
    bank_statement_file_id: null, proof_of_address_file_id: null,
    consent_credit_check: false, consent_terms: false, consent_popia: false,
    affordability: initAffordability(),
  });

  const [fileNames, setFileNames] = useState({
    id_document: '', payslip: '', bank_statement: '', proof_of_address: ''
  });

  // Auto-save form to sessionStorage on every change
  useEffect(() => {
    sessionStorage.setItem('2k2day_apply_form', JSON.stringify(form));
  }, [form]);

  // Capture agent referral code from URL (survives login redirect)
  useEffect(() => {
    const ref = new URLSearchParams(window.location.search).get('ref');
    if (ref) sessionStorage.setItem('2k2day_ref', ref);
  }, []);

  // Restore form from sessionStorage on mount
  useEffect(() => {
    const saved = sessionStorage.getItem('2k2day_apply_form');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        setForm(prev => ({ ...prev, ...parsed }));
      } catch { /* ignore */ }
    }
    // Pre-approval hand-off: ?amount=X carries the checked amount into the form
    const amt = parseFloat(new URLSearchParams(window.location.search).get('amount'));
    if (amt > 0) setForm(prev => ({ ...prev, loan_amount: String(amt) }));
  }, []);

  // Restore saved draft from server (logged-in clients) — server draft wins.
  // If no draft, pre-fill profile details from their last application (documents stay fresh).
  useEffect(() => {
    if (!user || user.role !== 'client') return;
    if (new URLSearchParams(window.location.search).get('edit')) return;
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/client/application-draft`, getAuthHeader());
        if (res.data.exists) {
          setForm(prev => ({ ...prev, ...res.data.form }));
          if (typeof res.data.step === 'number') setStep(res.data.step);
          setHasDraft(true);
          toast.success('Saved application restored — continue where you left off');
          return;
        }
        const pre = await axios.get(`${API_URL}/client/profile-prefill`, getAuthHeader());
        if (pre.data.exists) {
          const profile = pre.data.profile || {};
          setForm(prev => {
            const merged = { ...prev };
            Object.entries(profile).forEach(([k, v]) => {
              if (merged[k] === '' || merged[k] === null || merged[k] === undefined) merged[k] = v;
            });
            return merged;
          });
          toast.success('Your saved details were filled in — review, upload fresh documents and apply');
        }
      } catch { /* ignore */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const saveDraft = async (goToDashboard = true) => {
    if (!user) { toast.error('Log in to save your application for later'); return; }
    setSavingDraft(true);
    try {
      await axios.post(`${API_URL}/client/application-draft`, { form, step }, getAuthHeader());
      setHasDraft(true);
      toast.success('Application saved — finish it anytime from your dashboard');
      if (goToDashboard) navigate('/client/dashboard');
    } catch { toast.error('Could not save draft'); }
    finally { setSavingDraft(false); }
  };

  // Pre-fill from user data if logged in
  useEffect(() => {
    if (user) {
      const names = (user.full_name || '').split(' ');
      setForm(prev => ({
        ...prev,
        first_name: prev.first_name || names[0] || '',
        last_name: prev.last_name || names.slice(1).join(' ') || '',
        email: prev.email || user.email || '',
        cell_number: prev.cell_number || user.phone || '',
        id_number: prev.id_number || user.id_number || '',
      }));
    }
  }, [user]);

  // Load existing application if editing
  useEffect(() => {
    const appId = new URLSearchParams(window.location.search).get('edit');
    if (appId && token) {
      axios.get(`${API_URL}/client/applications/${appId}`, getAuthHeader())
        .then(res => {
          setExistingApp(res.data);
          const a = res.data;
          setForm(prev => ({ ...prev, ...a }));
        })
        .catch(() => toast.error('Could not load application'));
    }
  }, [token]);

  const updateForm = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const updateAffordability = (category, item, field, value) => {
    setForm(prev => {
      const aff = { ...prev.affordability };
      const cat = { ...aff[category] };
      const row = { ...cat[item], [field]: value };
      // Auto-calculate final as average of non-empty months
      if (['month1', 'month2', 'month3'].includes(field)) {
        const vals = [row.month1, row.month2, row.month3].map(v => parseFloat(v) || 0).filter((_, i) => [row.month1, row.month2, row.month3][i] !== '');
        row.final = vals.length > 0 ? (vals.reduce((a, b) => a + b, 0) / vals.length).toFixed(2) : '';
      }
      cat[item] = row;
      aff[category] = cat;
      return { ...prev, affordability: aff };
    });
  };

  const handleFileUpload = async (e, fieldKey) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!user || !token) {
      toast.error('Please log in or register to upload documents');
      return;
    }

    const allowed = ['image/jpeg', 'image/png', 'image/jpg', 'application/pdf'];
    if (!allowed.includes(file.type)) {
      toast.error('Only JPG, PNG and PDF files are allowed');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File too large (max 10MB)');
      return;
    }

    setUploadingField(fieldKey);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const res = await axios.post(`${API_URL}/client/upload-document`, formData, {
        ...getAuthHeader(),
        headers: { ...getAuthHeader().headers, 'Content-Type': 'multipart/form-data' }
      });
      updateForm(`${fieldKey}_file_id`, res.data.file_id);
      setFileNames(prev => ({ ...prev, [fieldKey]: file.name }));
      toast.success(`${file.name} uploaded`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploadingField(null);
    }
  };

  const handleSubmit = async () => {
    if (!form.consent_credit_check || !form.consent_terms || !form.consent_popia) {
      toast.error('Please accept all consent checkboxes');
      return;
    }

    // Not logged in as client → show inline auth
    if (!user || !token || user.role !== 'client') {
      setShowInlineAuth(true);
      return;
    }

    // Duplicate check: warn if an identical open application already exists
    if (!(await confirmNoDuplicate())) return;

    await submitApplication();
  };

  const confirmNoDuplicate = async () => {
    if (existingApp) return true;
    try {
      const res = await axios.get(`${API_URL}/client/applications`, getAuthHeader());
      const open = (res.data || []).filter(a => ['submitted', 'pending', 'under_review', 'awaiting_documents'].includes(a.status));
      const dup = open.find(a => Number(a.loan_amount) === (parseFloat(form.loan_amount) || 0)) || open[0];
      if (dup) {
        const same = Number(dup.loan_amount) === (parseFloat(form.loan_amount) || 0);
        const msg = same
          ? `You already have an open application (#${dup.application_number}) for R${Number(dup.loan_amount).toLocaleString()} that is still being processed.\n\nSubmitting again will create a DUPLICATE. Do you still want to submit a new application?`
          : `You already have an open application (#${dup.application_number}) that is still being processed.\n\nAre you sure you want to submit another one?`;
        if (!window.confirm(msg)) return false;
      }
    } catch { /* if check fails, allow submission */ }
    return true;
  };

  const submitApplication = async () => {
    setIsSubmitting(true);
    try {
      const payload = {
        ...form,
        gross_income: parseFloat(form.gross_income) || 0,
        net_income: parseFloat(form.net_income) || 0,
        total_expenses: parseFloat(form.total_expenses) || 0,
        total_deductions: parseFloat(form.total_deductions) || 0,
        loan_amount: parseFloat(form.loan_amount) || 2000,
        repayment_period: parseInt(form.repayment_period) || 30,
        dependants: parseInt(form.dependants) || 0,
        years_at_address: parseInt(form.years_at_address) || 0,
        affordability: form.affordability || null,
        referral_code: sessionStorage.getItem('2k2day_ref') || null,
      };

      if (existingApp) {
        await axios.put(`${API_URL}/client/applications/${existingApp.id}`, payload, getAuthHeader());
        toast.success('Application updated successfully!');
      } else {
        const res = await axios.post(`${API_URL}/client/apply`, payload, getAuthHeader());
        toast.success(`Application ${res.data.application_number} submitted!`);
      }
      sessionStorage.removeItem('2k2day_apply_form');
      sessionStorage.removeItem('2k2day_ref');
      navigate('/client/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Submission failed');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInlineAuth = async (e) => {
    e.preventDefault();
    setAuthLoading(true);
    try {
      if (authMode === 'register') {
        if (regForm.password.length < 6) {
          toast.error('Password must be at least 6 characters');
          setAuthLoading(false);
          return;
        }
        if (regForm.password !== regForm.confirmPassword) {
          toast.error('Passwords do not match');
          setAuthLoading(false);
          return;
        }
        await register({
          full_name: `${form.first_name} ${form.last_name}`,
          email: form.email,
          phone: form.cell_number,
          id_number: form.id_number,
          password: regForm.password,
        });
        toast.success('Account created! Submitting your application...');
      } else {
        const userData = await login(regForm.loginEmail, regForm.loginPassword);
        if (userData.role !== 'client') {
          toast.error('Please use a client account');
          setAuthLoading(false);
          return;
        }
        toast.success('Logged in! Submitting your application...');
      }
      setShowInlineAuth(false);
      // Small delay to let auth context update
      setTimeout(async () => {
        if (await confirmNoDuplicate()) submitApplication();
      }, 300);
    } catch (err) {
      toast.error(err.response?.data?.detail || (authMode === 'register' ? 'Registration failed' : 'Login failed'));
    } finally {
      setAuthLoading(false);
    }
  };

  const nextStep = () => {
    if (step === 0 && (!form.first_name || !form.last_name || !form.id_number)) {
      toast.error('Please fill in your name and ID number');
      return;
    }
    if (step === 1 && !form.cell_number) {
      toast.error('Please enter your cell number');
      return;
    }
    if (step === 4) {
      if (!form.bank_name) { toast.error('Please select your bank'); return; }
      if (!form.account_type) { toast.error('Please select your account type'); return; }
      if (!form.account_number.trim()) { toast.error('Please enter your account number'); return; }
      if (!/^\d{6}$/.test((form.branch_code || '').trim())) { toast.error('A six-digit branch code is required — select your bank to fill it automatically'); return; }
    }
    if (step < STEPS.length - 1) setStep(step + 1);
  };

  const prevStep = () => { if (step > 0) setStep(step - 1); };

  const fees = calculateFees(parseFloat(form.loan_amount) || 2000);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="loan-application-page">
      {/* Header */}
      <header className="bg-[#0f2b4a] text-white sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center space-x-2" data-testid="app-logo">
            <div className="w-9 h-9 bg-white/10 rounded-lg flex items-center justify-center">
              <Banknote className="w-5 h-5 text-emerald-400" />
            </div>
            <span className="text-xl font-bold tracking-tight" style={{ fontFamily: 'Manrope' }}>2k2day</span>
          </Link>
          <div className="flex items-center gap-3">
            {user ? (
              <span className="text-sm text-white/70">{user.full_name}</span>
            ) : (
              <Link to="/client/login">
                <Button size="sm" variant="outline" className="text-white border-white/30 hover:bg-white/10 bg-transparent" data-testid="login-btn">
                  Login / Register
                </Button>
              </Link>
            )}
          </div>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 py-6">
        {/* Title */}
        <div className="mb-6">
          <Link to="/" className="text-sm text-slate-500 hover:text-emerald-700 flex items-center gap-1 mb-3" data-testid="back-link">
            <ArrowLeft className="w-4 h-4" /> Back to Home
          </Link>
          <h1 className="text-2xl sm:text-3xl font-bold text-[#0f2b4a] tracking-tight" style={{ fontFamily: 'Manrope' }} data-testid="page-title">
            {existingApp ? 'Edit Application' : 'Loan Application'}
          </h1>
          <p className="text-slate-500 mt-1 text-sm">Complete all sections to submit your application</p>
        </div>

        {/* Step Progress */}
        <div className="flex items-center gap-1 mb-6 overflow-x-auto pb-2" data-testid="step-progress">
          {STEPS.map((s, i) => {
            const Icon = s.icon;
            const isActive = i === step;
            const isDone = i < step;
            return (
              <button
                key={s.id}
                onClick={() => i <= step && setStep(i)}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium whitespace-nowrap transition-all ${
                  isActive ? 'bg-[#0f2b4a] text-white' :
                  isDone ? 'bg-emerald-100 text-emerald-800' :
                  'bg-white text-slate-400 border border-slate-200'
                }`}
                data-testid={`step-${s.id}`}
              >
                {isDone ? <Check className="w-3.5 h-3.5" /> : <Icon className="w-3.5 h-3.5" />}
                <span className="hidden sm:inline">{s.label}</span>
              </button>
            );
          })}
        </div>

        {/* Auth Notice */}
        {(!user || user.role !== 'client') && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6 flex items-start gap-3" data-testid="auth-notice">
            <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-amber-800">Login required to submit</p>
              <p className="text-xs text-amber-600 mt-0.5">
                Fill out the form — your progress is saved automatically. When you click Submit, you&apos;ll be asked to{' '}
                <Link to="/client/login?redirect=/apply" className="underline font-medium">log in or register</Link>{' '}
                and then you can complete your application.
              </p>
            </div>
          </div>
        )}

        {/* Form Card */}
        <Card className="border-slate-200 shadow-sm">
          <CardHeader className="bg-[#0f2b4a] text-white rounded-t-lg py-4">
            <CardTitle className="flex items-center gap-2 text-lg" style={{ fontFamily: 'Manrope' }}>
              {(() => { const Icon = STEPS[step].icon; return <Icon className="w-5 h-5" />; })()}
              {STEPS[step].label} Details
            </CardTitle>
          </CardHeader>

          <CardContent className="p-6">
            {/* Step 0: Personal */}
            {step === 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="step-personal-form">
                <FormField label="Title" testId="title">
                  <Select value={form.title} onValueChange={v => updateForm('title', v)}>
                    <SelectTrigger data-testid="title-select"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      {['Mr', 'Mrs', 'Ms', 'Dr', 'Prof'].map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </FormField>
                <FormField label="First Name *" testId="first-name">
                  <Input value={form.first_name} onChange={e => updateForm('first_name', e.target.value)} required data-testid="first-name-input" />
                </FormField>
                <FormField label="Last Name *" testId="last-name">
                  <Input value={form.last_name} onChange={e => updateForm('last_name', e.target.value)} required data-testid="last-name-input" />
                </FormField>
                <FormField label="SA ID Number *" testId="id-number">
                  <Input value={form.id_number} onChange={e => updateForm('id_number', e.target.value)} maxLength={13} required data-testid="id-number-input" />
                </FormField>
                <FormField label="Date of Birth" testId="dob">
                  <Input type="date" value={form.date_of_birth} onChange={e => updateForm('date_of_birth', e.target.value)} data-testid="dob-input" />
                </FormField>
                <FormField label="Gender" testId="gender">
                  <Select value={form.gender} onValueChange={v => updateForm('gender', v)}>
                    <SelectTrigger data-testid="gender-select"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      {['Male', 'Female', 'Other'].map(g => <SelectItem key={g} value={g}>{g}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </FormField>
                <FormField label="Marital Status" testId="marital">
                  <Select value={form.marital_status} onValueChange={v => updateForm('marital_status', v)}>
                    <SelectTrigger data-testid="marital-select"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      {['Single', 'Married', 'Divorced', 'Widowed', 'Living Together'].map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </FormField>
                <FormField label="Number of Dependants" testId="dependants">
                  <Input type="number" min="0" value={form.dependants} onChange={e => updateForm('dependants', e.target.value)} data-testid="dependants-input" />
                </FormField>
              </div>
            )}

            {/* Step 1: Contact */}
            {step === 1 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="step-contact-form">
                <FormField label="Cell Number *" testId="cell">
                  <Input value={form.cell_number} onChange={e => updateForm('cell_number', e.target.value)} placeholder="082 123 4567" required data-testid="cell-input" />
                </FormField>
                <FormField label="Alternative Number" testId="alt-number">
                  <Input value={form.alt_number} onChange={e => updateForm('alt_number', e.target.value)} placeholder="011 123 4567" data-testid="alt-number-input" />
                </FormField>
                <FormField label="Email Address *" testId="email" className="sm:col-span-2">
                  <Input type="email" value={form.email} onChange={e => updateForm('email', e.target.value)} required data-testid="email-input" />
                </FormField>
              </div>
            )}

            {/* Step 2: Address */}
            {step === 2 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="step-address-form">
                <FormField label="Street Address" testId="street" className="sm:col-span-2">
                  <Input value={form.street_address} onChange={e => updateForm('street_address', e.target.value)} data-testid="street-input" />
                </FormField>
                <FormField label="Suburb" testId="suburb">
                  <Input value={form.suburb} onChange={e => updateForm('suburb', e.target.value)} data-testid="suburb-input" />
                </FormField>
                <FormField label="City / Town" testId="city">
                  <Input value={form.city} onChange={e => updateForm('city', e.target.value)} data-testid="city-input" />
                </FormField>
                <FormField label="Province" testId="province">
                  <Select value={form.province} onValueChange={v => updateForm('province', v)}>
                    <SelectTrigger data-testid="province-select"><SelectValue placeholder="Select province" /></SelectTrigger>
                    <SelectContent>
                      {SA_PROVINCES.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </FormField>
                <FormField label="Postal Code" testId="postal">
                  <Input value={form.postal_code} onChange={e => updateForm('postal_code', e.target.value)} maxLength={4} data-testid="postal-input" />
                </FormField>
                <FormField label="Residential Status" testId="res-status">
                  <Select value={form.residential_status} onValueChange={v => updateForm('residential_status', v)}>
                    <SelectTrigger data-testid="res-status-select"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      {['Owner', 'Renting', 'Boarding', 'Living with Family', 'Other'].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </FormField>
                <FormField label="Years at Current Address" testId="years-addr">
                  <Input type="number" min="0" value={form.years_at_address} onChange={e => updateForm('years_at_address', e.target.value)} data-testid="years-addr-input" />
                </FormField>
              </div>
            )}

            {/* Step 3: Employment */}
            {step === 3 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="step-employment-form">
                <FormField label="Employment Status" testId="emp-status">
                  <Select value={form.employment_status} onValueChange={v => updateForm('employment_status', v)}>
                    <SelectTrigger data-testid="emp-status-select"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      {['Employed', 'Self-employed', 'Part-time', 'Contract', 'Unemployed', 'Retired', 'Student'].map(s =>
                        <SelectItem key={s} value={s.toLowerCase()}>{s}</SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                </FormField>
                <FormField label="Employer / Company Name" testId="employer">
                  <Input value={form.employer_name} onChange={e => updateForm('employer_name', e.target.value)} data-testid="employer-input" />
                </FormField>
                <FormField label="Occupation / Job Title" testId="occupation">
                  <Input value={form.occupation} onChange={e => updateForm('occupation', e.target.value)} data-testid="occupation-input" />
                </FormField>
                <FormField label="Employment Start Date" testId="emp-date">
                  <Input type="date" value={form.employment_date} onChange={e => updateForm('employment_date', e.target.value)} data-testid="emp-date-input" />
                </FormField>
                <FormField label="Salary Date (Day of Month)" testId="salary-date">
                  <Input value={form.salary_date} onChange={e => updateForm('salary_date', e.target.value)} placeholder="e.g. 25" data-testid="salary-date-input" />
                </FormField>

                <div className="sm:col-span-2 mt-2 border-t border-slate-100 pt-4">
                  <p className="text-sm font-semibold text-[#0f2b4a] mb-1">Employer Contact Details</p>
                  <p className="text-xs text-slate-400 mb-3">Who can we contact at your workplace to confirm your employment?</p>
                </div>
                <FormField label="Contact Person Name" testId="emp-contact-person">
                  <Input value={form.employer_contact_person} onChange={e => updateForm('employer_contact_person', e.target.value)} placeholder="e.g. HR manager or supervisor" data-testid="emp-contact-person-input" />
                </FormField>
                <FormField label="Employer Phone" testId="emp-phone">
                  <Input value={form.employer_phone} onChange={e => updateForm('employer_phone', e.target.value)} data-testid="emp-phone-input" />
                </FormField>
                <FormField label="Employer Email" testId="emp-email">
                  <Input type="email" value={form.employer_email} onChange={e => updateForm('employer_email', e.target.value)} data-testid="emp-email-input" />
                </FormField>
                <FormField label="Company Street Address" testId="emp-address">
                  <Input value={form.employer_address} onChange={e => updateForm('employer_address', e.target.value)} data-testid="emp-address-input" />
                </FormField>
                <FormField label="Suburb" testId="emp-suburb">
                  <Input value={form.employer_suburb} onChange={e => updateForm('employer_suburb', e.target.value)} data-testid="emp-suburb-input" />
                </FormField>
                <FormField label="City / Town" testId="emp-city">
                  <Input value={form.employer_city} onChange={e => updateForm('employer_city', e.target.value)} data-testid="emp-city-input" />
                </FormField>
                <FormField label="Postal Code" testId="emp-postal">
                  <Input value={form.employer_postal_code} onChange={e => updateForm('employer_postal_code', e.target.value)} data-testid="emp-postal-input" />
                </FormField>
              </div>
            )}

            {/* Step 4: Bank */}
            {step === 4 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="step-bank-form">
                <FormField label="Bank Name *" testId="bank-name">
                  <Select value={form.bank_name} onValueChange={v => {
                    updateForm('bank_name', v);
                    if (BANK_BRANCH_CODES[v]) updateForm('branch_code', BANK_BRANCH_CODES[v]);
                    else if (v === 'Other') updateForm('branch_code', '');
                  }}>
                    <SelectTrigger data-testid="bank-name-select"><SelectValue placeholder="Select bank" /></SelectTrigger>
                    <SelectContent>
                      {SA_BANKS.map(b => <SelectItem key={b} value={b}>{b}{BANK_BRANCH_CODES[b] ? ` (${BANK_BRANCH_CODES[b]})` : ''}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </FormField>
                <FormField label="Account Type *" testId="acc-type">
                  <Select value={form.account_type} onValueChange={v => updateForm('account_type', v)}>
                    <SelectTrigger data-testid="acc-type-select"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      {['Savings', 'Cheque', 'Current', 'Transmission'].map(t =>
                        <SelectItem key={t} value={t.toLowerCase()}>{t}</SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                </FormField>
                <FormField label="Account Number *" testId="acc-number">
                  <Input value={form.account_number} onChange={e => updateForm('account_number', e.target.value.replace(/\D/g, ''))} required data-testid="acc-number-input" />
                </FormField>
                <FormField label="Branch Code *" testId="branch-code">
                  <Input value={form.branch_code}
                    onChange={e => updateForm('branch_code', e.target.value.replace(/\D/g, '').slice(0, 6))}
                    readOnly={!!BANK_BRANCH_CODES[form.bank_name]}
                    placeholder={form.bank_name === 'Other' ? 'Enter 6-digit branch code' : 'Auto-filled when you select your bank'}
                    className={BANK_BRANCH_CODES[form.bank_name] ? 'bg-slate-50 text-slate-600' : ''}
                    required data-testid="branch-code-input" />
                  {BANK_BRANCH_CODES[form.bank_name] && (
                    <p className="text-[11px] text-emerald-700 mt-1" data-testid="branch-code-autofill-note">Universal branch code for {form.bank_name} filled in automatically</p>
                  )}
                </FormField>
                <FormField label="Account Holder Name" testId="acc-holder" className="sm:col-span-2">
                  <Input value={form.account_holder} onChange={e => updateForm('account_holder', e.target.value)} placeholder="If different from applicant" data-testid="acc-holder-input" />
                </FormField>
              </div>
            )}

            {/* Step 5: Financial */}
            {step === 5 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="step-financial-form">
                <FormField label="Gross Monthly Income (R)" testId="gross-income">
                  <Input type="number" min="0" value={form.gross_income} onChange={e => updateForm('gross_income', e.target.value)} data-testid="gross-income-input" />
                </FormField>
                <FormField label="Net Monthly Income (R)" testId="net-income">
                  <Input type="number" min="0" value={form.net_income} onChange={e => updateForm('net_income', e.target.value)} data-testid="net-income-input" />
                </FormField>
                <FormField label="Total Monthly Expenses (R)" testId="expenses">
                  <Input type="number" min="0" value={form.total_expenses} onChange={e => updateForm('total_expenses', e.target.value)} data-testid="expenses-input" />
                </FormField>
                <FormField label="Total Monthly Deductions (R)" testId="deductions">
                  <Input type="number" min="0" value={form.total_deductions} onChange={e => updateForm('total_deductions', e.target.value)} data-testid="deductions-input" />
                </FormField>
                <FormField label="Existing Loans / Debt" testId="existing-loans" className="sm:col-span-2">
                  <Input value={form.existing_loans} onChange={e => updateForm('existing_loans', e.target.value)} placeholder="e.g. Car finance R2,500/month" data-testid="existing-loans-input" />
                </FormField>
                {form.net_income && form.total_expenses && (
                  <div className="sm:col-span-2 bg-slate-50 rounded-lg p-4 border border-slate-200">
                    <p className="text-sm text-slate-600">
                      Disposable Income: <span className="font-bold text-emerald-700">
                        R{((parseFloat(form.net_income) || 0) - (parseFloat(form.total_expenses) || 0) - (parseFloat(form.total_deductions) || 0)).toLocaleString('en-ZA', { minimumFractionDigits: 2 })}
                      </span>
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Step 6: Loan */}
            {step === 6 && (
              <div className="space-y-6" data-testid="step-loan-form">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <FormField label="Loan Amount (R)" testId="loan-amount">
                    <Input type="number" min="200" max="5000" value={form.loan_amount} onChange={e => updateForm('loan_amount', e.target.value)} data-testid="loan-amount-input" />
                  </FormField>
                  <FormField label="Repayment Period (Days)" testId="repayment-period">
                    <Select value={String(form.repayment_period)} onValueChange={v => updateForm('repayment_period', v)}>
                      <SelectTrigger data-testid="repayment-period-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {[7, 14, 21, 30, 60, 90].map(d => <SelectItem key={d} value={String(d)}>{d} days</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </FormField>
                  <FormField label="Loan Purpose" testId="loan-purpose" className="sm:col-span-2">
                    <Input value={form.loan_purpose} onChange={e => updateForm('loan_purpose', e.target.value)} placeholder="e.g. Emergency, Medical, Education" data-testid="loan-purpose-input" />
                  </FormField>
                </div>

                {/* Fee Breakdown */}
                <div className="bg-[#0f2b4a] text-white rounded-lg p-5" data-testid="fee-breakdown">
                  <h3 className="font-semibold mb-3 text-sm uppercase tracking-wider opacity-80">Fee Breakdown</h3>
                  <div className="space-y-2 text-sm">
                    <FeeRow label="Principal" value={fees.principal} />
                    <FeeRow label="Initiation Fee" value={fees.initiation_fee} />
                    <FeeRow label="Interest (5%)" value={fees.interest} />
                    <FeeRow label="Admin Fee" value={fees.admin_fee} />
                    <FeeRow label="Insurance (3%)" value={fees.insurance} />
                    <div className="border-t border-white/20 my-2"></div>
                    <FeeRow label="Total Fees" value={fees.total_fees} bold />
                    <FeeRow label="Total Repayment" value={fees.total_repayment} bold accent />
                  </div>
                </div>

                {/* Smart Pre-Approval Indicator */}
                {(parseFloat(form.net_income) > 0) && (() => {
                  const netIncome = parseFloat(form.net_income) || 0;
                  const expenses = parseFloat(form.total_expenses) || 0;
                  const deductions = parseFloat(form.total_deductions) || 0;
                  const disposable = netIncome - expenses - deductions;
                  const repayment = fees.total_repayment;
                  const ratio = disposable > 0 ? (repayment / disposable) * 100 : 999;
                  const canAfford = disposable > 0 && ratio < 50;
                  const isRisky = ratio >= 50 && ratio < 80;
                  const score = canAfford ? 'HIGH' : isRisky ? 'MEDIUM' : 'LOW';
                  const color = canAfford ? 'emerald' : isRisky ? 'amber' : 'red';
                  const barWidth = Math.min(100, Math.max(5, canAfford ? 85 : isRisky ? 50 : 20));

                  return (
                    <div className={`border rounded-lg p-4 bg-${color}-50 border-${color}-200`} data-testid="affordability-indicator"
                      style={{ backgroundColor: canAfford ? '#ecfdf5' : isRisky ? '#fffbeb' : '#fef2f2', borderColor: canAfford ? '#a7f3d0' : isRisky ? '#fde68a' : '#fecaca' }}>
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="text-sm font-semibold" style={{ color: canAfford ? '#065f46' : isRisky ? '#92400e' : '#991b1b' }}>
                          Pre-Approval Estimate
                        </h4>
                        <span className="text-xs font-bold px-2.5 py-1 rounded-full"
                          style={{ backgroundColor: canAfford ? '#059669' : isRisky ? '#d97706' : '#dc2626', color: 'white' }}>
                          {score} LIKELIHOOD
                        </span>
                      </div>
                      <div className="h-2.5 bg-slate-200 rounded-full overflow-hidden mb-3">
                        <div className="h-full rounded-full transition-all duration-500"
                          style={{ width: `${barWidth}%`, backgroundColor: canAfford ? '#059669' : isRisky ? '#d97706' : '#dc2626' }} />
                      </div>
                      <div className="grid grid-cols-3 gap-3 text-xs">
                        <div>
                          <span className="text-slate-500">Disposable Income</span>
                          <p className="font-bold" style={{ color: disposable > 0 ? '#065f46' : '#991b1b' }}>
                            R{disposable.toLocaleString('en-ZA', { minimumFractionDigits: 2 })}
                          </p>
                        </div>
                        <div>
                          <span className="text-slate-500">Repayment Amount</span>
                          <p className="font-bold text-slate-800">R{repayment.toLocaleString('en-ZA', { minimumFractionDigits: 2 })}</p>
                        </div>
                        <div>
                          <span className="text-slate-500">Debt-to-Income</span>
                          <p className="font-bold text-slate-800">{ratio < 999 ? `${ratio.toFixed(0)}%` : 'N/A'}</p>
                        </div>
                      </div>
                      <p className="text-xs mt-2" style={{ color: canAfford ? '#065f46' : isRisky ? '#92400e' : '#991b1b' }}>
                        {canAfford
                          ? 'Based on your income and expenses, this loan appears affordable. Final approval is subject to verification.'
                          : isRisky
                          ? 'This repayment may stretch your budget. Consider a smaller amount or longer repayment period.'
                          : 'Your expenses exceed your income, making repayment difficult. We recommend reviewing your budget or applying for a smaller amount.'}
                      </p>
                    </div>
                  );
                })()}

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <FormField label="Preferred Debit Order Date" testId="do-date">
                    <Input value={form.debit_order_date} onChange={e => updateForm('debit_order_date', e.target.value)} placeholder="e.g. 25th" data-testid="do-date-input" />
                  </FormField>
                  <FormField label="Debit Order Account" testId="do-account">
                    <Input value={form.debit_order_account} onChange={e => updateForm('debit_order_account', e.target.value)} placeholder="Same as bank account if blank" data-testid="do-account-input" />
                  </FormField>
                </div>
              </div>
            )}

            {/* Step 7: Documents */}
            {step === 7 && (
              <div className="space-y-4" data-testid="step-documents-form">
                {!user && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
                    <AlertCircle className="w-4 h-4 inline mr-1" />
                    You must be logged in to upload documents.{' '}
                    <Link to="/client/login" className="underline font-medium">Login / Register</Link>
                  </div>
                )}
                <DocUpload label="SA ID Document (Copy)" fieldKey="id_document" fileName={fileNames.id_document}
                  fileId={form.id_document_file_id} uploading={uploadingField === 'id_document'}
                  onUpload={e => handleFileUpload(e, 'id_document')} disabled={!user} />
                <DocUpload label="Latest Payslip" fieldKey="payslip" fileName={fileNames.payslip}
                  fileId={form.payslip_file_id} uploading={uploadingField === 'payslip'}
                  onUpload={e => handleFileUpload(e, 'payslip')} disabled={!user} />
                <DocUpload label="3-Month Bank Statement" fieldKey="bank_statement" fileName={fileNames.bank_statement}
                  fileId={form.bank_statement_file_id} uploading={uploadingField === 'bank_statement'}
                  onUpload={e => handleFileUpload(e, 'bank_statement')} disabled={!user} />
                <DocUpload label="Proof of Address" fieldKey="proof_of_address" fileName={fileNames.proof_of_address}
                  fileId={form.proof_of_address_file_id} uploading={uploadingField === 'proof_of_address'}
                  onUpload={e => handleFileUpload(e, 'proof_of_address')} disabled={!user} />
              </div>
            )}

            {/* Step 8: Loan Affordability Details */}
            {step === 8 && (() => {
              const months = getMonthLabels();
              return (
                <div className="space-y-6" data-testid="step-affordability-form">
                  <p className="text-sm text-slate-500">Enter your income, deductions, expenses and debt for the last 3 months. The Final column auto-calculates the average.</p>
                  {AFFORDABILITY_SECTIONS.map(section => {
                    const borderColor = section.color === 'emerald' ? 'border-emerald-400' : section.color === 'blue' ? 'border-blue-400' : section.color === 'amber' ? 'border-amber-400' : 'border-red-400';
                    const bgColor = section.color === 'emerald' ? 'bg-emerald-50' : section.color === 'blue' ? 'bg-blue-50' : section.color === 'amber' ? 'bg-amber-50' : 'bg-red-50';
                    const textColor = section.color === 'emerald' ? 'text-emerald-800' : section.color === 'blue' ? 'text-blue-800' : section.color === 'amber' ? 'text-amber-800' : 'text-red-800';
                    return (
                      <div key={section.key} className={`border-l-4 ${borderColor} rounded-lg overflow-hidden`} data-testid={`aff-section-${section.key}`}>
                        <div className={`${bgColor} px-4 py-2`}>
                          <h4 className={`font-semibold text-sm ${textColor}`}>{section.label}</h4>
                        </div>
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="border-b border-slate-200 bg-slate-50">
                                <th className="text-left py-2 px-3 text-xs text-slate-500 font-medium w-[140px]">Item</th>
                                <th className="text-right py-2 px-2 text-xs text-slate-500 font-medium">{months[0]}</th>
                                <th className="text-right py-2 px-2 text-xs text-slate-500 font-medium">{months[1]}</th>
                                <th className="text-right py-2 px-2 text-xs text-slate-500 font-medium">{months[2]}</th>
                                <th className="text-left py-2 px-2 text-xs text-slate-500 font-medium">Notes</th>
                                <th className="text-right py-2 px-3 text-xs text-slate-500 font-medium bg-slate-100">Final</th>
                              </tr>
                            </thead>
                            <tbody>
                              {section.items.map(item => {
                                const row = form.affordability?.[section.key]?.[item.key] || {};
                                return (
                                  <tr key={item.key} className="border-b border-slate-100 hover:bg-slate-50/50">
                                    <td className="py-1.5 px-3 text-xs font-medium text-slate-700">{item.label}</td>
                                    {['month1', 'month2', 'month3'].map(m => (
                                      <td key={m} className="py-1.5 px-1">
                                        <Input
                                          type="number" min="0" step="0.01"
                                          className="h-8 text-xs text-right w-[100px] tabular-nums"
                                          placeholder="0.00"
                                          value={row[m] || ''}
                                          onChange={e => updateAffordability(section.key, item.key, m, e.target.value)}
                                          data-testid={`aff-${item.key}-${m}`}
                                        />
                                      </td>
                                    ))}
                                    <td className="py-1.5 px-1">
                                      <Input
                                        className="h-8 text-xs w-[120px]"
                                        placeholder="Notes"
                                        value={row.notes || ''}
                                        onChange={e => updateAffordability(section.key, item.key, 'notes', e.target.value)}
                                        data-testid={`aff-${item.key}-notes`}
                                      />
                                    </td>
                                    <td className="py-1.5 px-2 bg-slate-50">
                                      <Input
                                        type="number" min="0" step="0.01"
                                        className="h-8 text-xs text-right w-[100px] tabular-nums font-semibold"
                                        placeholder="0.00"
                                        value={row.final || ''}
                                        onChange={e => updateAffordability(section.key, item.key, 'final', e.target.value)}
                                        data-testid={`aff-${item.key}-final`}
                                      />
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    );
                  })}

                  {/* Affordability Summary */}
                  {(() => {
                    const aff = form.affordability || {};
                    const sumFinal = (catKey) => {
                      const cat = aff[catKey] || {};
                      return Object.values(cat).reduce((sum, r) => sum + (parseFloat(r?.final) || 0), 0);
                    };
                    const totalIncome = sumFinal('income');
                    const totalDeductions = sumFinal('statutory_deductions');
                    const totalExpenses = sumFinal('expenses');
                    const totalDebt = sumFinal('debt_obligations');
                    const disposable = totalIncome - totalDeductions - totalExpenses - totalDebt;
                    return totalIncome > 0 ? (
                      <div className="p-4 bg-[#0f2b4a] text-white rounded-lg" data-testid="aff-summary">
                        <h4 className="font-semibold text-sm mb-3 uppercase tracking-wider opacity-80">Affordability Summary</h4>
                        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 text-sm">
                          <div><span className="text-white/60 text-xs">Total Income</span><p className="font-bold text-emerald-300">R{totalIncome.toFixed(2)}</p></div>
                          <div><span className="text-white/60 text-xs">Deductions</span><p className="font-medium">R{totalDeductions.toFixed(2)}</p></div>
                          <div><span className="text-white/60 text-xs">Expenses</span><p className="font-medium">R{totalExpenses.toFixed(2)}</p></div>
                          <div><span className="text-white/60 text-xs">Debt</span><p className="font-medium">R{totalDebt.toFixed(2)}</p></div>
                          <div><span className="text-white/60 text-xs">Disposable</span><p className={`font-bold text-lg ${disposable >= 0 ? 'text-emerald-300' : 'text-red-400'}`}>R{disposable.toFixed(2)}</p></div>
                        </div>
                      </div>
                    ) : null;
                  })()}
                </div>
              );
            })()}

            {/* Step 9: Consent & Submit */}
            {step === 9 && (
              <div className="space-y-6" data-testid="step-consent-form">
                {/* Application Summary */}
                <div className="bg-slate-50 rounded-lg p-5 border border-slate-200">
                  <h3 className="font-semibold text-[#0f2b4a] mb-3">Application Summary</h3>
                  <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
                    <SummaryRow label="Name" value={`${form.title} ${form.first_name} ${form.last_name}`} />
                    <SummaryRow label="ID Number" value={form.id_number} />
                    <SummaryRow label="Cell" value={form.cell_number} />
                    <SummaryRow label="Email" value={form.email} />
                    <SummaryRow label="Employer" value={form.employer_name} />
                    <SummaryRow label="Employer Contact" value={form.employer_contact_person ? `${form.employer_contact_person} · ${form.employer_phone || ''}` : form.employer_phone} />
                    <SummaryRow label="Net Income" value={`R${parseFloat(form.net_income || 0).toLocaleString()}`} />
                    <SummaryRow label="Bank" value={form.bank_name} />
                    <SummaryRow label="Account" value={form.account_number} />
                    <SummaryRow label="Loan Amount" value={`R${parseFloat(form.loan_amount || 0).toLocaleString()}`} />
                    <SummaryRow label="Total Repayment" value={`R${fees.total_repayment.toLocaleString('en-ZA', { minimumFractionDigits: 2 })}`} />
                  </div>
                </div>

                {/* Consent Checkboxes */}
                <div className="space-y-4">
                  <ConsentBox
                    testId="consent-credit"
                    checked={form.consent_credit_check}
                    onCheckedChange={v => updateForm('consent_credit_check', v)}
                    label="I consent to a credit check being performed and my information being shared with credit bureaus."
                  />
                  <ConsentBox
                    testId="consent-terms"
                    checked={form.consent_terms}
                    onCheckedChange={v => updateForm('consent_terms', v)}
                    label="I accept the terms and conditions of the loan agreement and confirm all information provided is accurate."
                  />
                  <ConsentBox
                    testId="consent-popia"
                    checked={form.consent_popia}
                    onCheckedChange={v => updateForm('consent_popia', v)}
                    label="I consent to the processing of my personal information in accordance with the Protection of Personal Information Act (POPIA)."
                  />
                </div>

                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 text-sm text-emerald-800">
                  <Shield className="w-4 h-4 inline mr-1" />
                  Your information is encrypted and securely stored. We will never share your data without your consent.
                </div>

                {/* Inline Registration / Login */}
                {showInlineAuth && (!user || user.role !== 'client') && (
                  <div className="bg-white border-2 border-[#0f2b4a] rounded-xl p-6 space-y-4 animate-in fade-in duration-300" data-testid="inline-auth-form">
                    <div className="flex items-center justify-between">
                      <h3 className="font-bold text-[#0f2b4a] text-lg" style={{ fontFamily: 'Manrope' }}>
                        {authMode === 'register' ? 'Create Account to Submit' : 'Login to Submit'}
                      </h3>
                      <button onClick={() => setShowInlineAuth(false)} className="text-slate-400 hover:text-slate-600">
                        <X className="w-5 h-5" />
                      </button>
                    </div>

                    {/* Auth mode tabs */}
                    <div className="flex bg-slate-100 rounded-lg p-1">
                      <button
                        onClick={() => setAuthMode('register')}
                        className={`flex-1 py-2 text-sm font-medium rounded-md transition ${authMode === 'register' ? 'bg-[#0f2b4a] text-white' : 'text-slate-600'}`}
                        data-testid="inline-register-tab"
                      >
                        Register
                      </button>
                      <button
                        onClick={() => setAuthMode('login')}
                        className={`flex-1 py-2 text-sm font-medium rounded-md transition ${authMode === 'login' ? 'bg-[#0f2b4a] text-white' : 'text-slate-600'}`}
                        data-testid="inline-login-tab"
                      >
                        Login
                      </button>
                    </div>

                    <form onSubmit={handleInlineAuth} className="space-y-3">
                      {authMode === 'register' ? (
                        <>
                          <div className="bg-slate-50 rounded-lg p-3 text-xs text-slate-600">
                            <p><strong>Name:</strong> {form.first_name} {form.last_name}</p>
                            <p><strong>Email:</strong> {form.email}</p>
                            <p><strong>Phone:</strong> {form.cell_number}</p>
                          </div>
                          {!form.email && (
                            <div className="text-red-600 text-xs flex items-center gap-1">
                              <AlertCircle className="w-3.5 h-3.5" /> Please go back and fill in your email address first
                            </div>
                          )}
                          <FormField label="Create Password *" testId="reg-password">
                            <div className="relative">
                              <Input
                                type={showPassword ? 'text' : 'password'}
                                value={regForm.password}
                                onChange={e => setRegForm(p => ({ ...p, password: e.target.value }))}
                                placeholder="Min 6 characters"
                                required
                                data-testid="reg-password-input"
                              />
                              <button type="button" onClick={() => setShowPassword(!showPassword)}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                              </button>
                            </div>
                          </FormField>
                          <FormField label="Confirm Password *" testId="reg-confirm-password">
                            <Input
                              type="password"
                              value={regForm.confirmPassword}
                              onChange={e => setRegForm(p => ({ ...p, confirmPassword: e.target.value }))}
                              placeholder="Re-enter password"
                              required
                              data-testid="reg-confirm-password-input"
                            />
                          </FormField>
                        </>
                      ) : (
                        <>
                          <FormField label="Email" testId="login-email">
                            <Input
                              type="email"
                              value={regForm.loginEmail}
                              onChange={e => setRegForm(p => ({ ...p, loginEmail: e.target.value }))}
                              placeholder="your@email.com"
                              required
                              data-testid="login-email-input"
                            />
                          </FormField>
                          <FormField label="Password" testId="login-password">
                            <div className="relative">
                              <Input
                                type={showPassword ? 'text' : 'password'}
                                value={regForm.loginPassword}
                                onChange={e => setRegForm(p => ({ ...p, loginPassword: e.target.value }))}
                                placeholder="Your password"
                                required
                                data-testid="login-password-input"
                              />
                              <button type="button" onClick={() => setShowPassword(!showPassword)}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                              </button>
                            </div>
                          </FormField>
                        </>
                      )}
                      <Button
                        type="submit"
                        disabled={authLoading || (authMode === 'register' && !form.email)}
                        className="w-full h-11 bg-emerald-700 hover:bg-emerald-800"
                        data-testid="inline-auth-submit-btn"
                      >
                        {authLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
                        {authMode === 'register' ? 'Create Account & Submit' : 'Login & Submit'}
                      </Button>
                    </form>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Navigation */}
        <div className="flex items-center justify-between mt-6 pb-8">
          <Button
            variant="outline"
            onClick={prevStep}
            disabled={step === 0}
            className="h-11 px-6"
            data-testid="prev-step-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" /> Previous
          </Button>

          {step < STEPS.length - 1 ? (
            <div className="flex items-center gap-2">
              {user?.role === 'client' && (
                <Button
                  variant="outline"
                  onClick={() => saveDraft(true)}
                  disabled={savingDraft}
                  className="h-11 px-4 border-slate-300 text-slate-600"
                  data-testid="save-draft-btn"
                >
                  <Save className="w-4 h-4 mr-2" /> {savingDraft ? 'Saving...' : 'Save & Finish Later'}
                </Button>
              )}
              <Button
                onClick={nextStep}
                className="h-11 px-6 bg-[#0f2b4a] hover:bg-[#1a3d5c]"
                data-testid="next-step-btn"
              >
                Next <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          ) : (
            <Button
              onClick={handleSubmit}
              disabled={isSubmitting || !form.consent_credit_check || !form.consent_terms || !form.consent_popia}
              className="h-11 px-8 bg-emerald-700 hover:bg-emerald-800"
              data-testid="submit-application-btn"
            >
              {isSubmitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
              {existingApp ? 'Update Application' : 'Submit Application'}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

// Helpers
function calculateFees(principal) {
  const p = Math.max(200, Math.min(5000, principal || 2000));
  const initiation_fee = p <= 1000 ? p * 0.15 : 1000 * 0.15 + (p - 1000) * 0.10;
  const interest = p * 0.05;
  const admin_fee = 60;
  const insurance = p * 0.03;
  const total_fees = initiation_fee + interest + admin_fee + insurance;
  return {
    principal: p,
    initiation_fee: Math.round(initiation_fee * 100) / 100,
    interest: Math.round(interest * 100) / 100,
    admin_fee,
    insurance: Math.round(insurance * 100) / 100,
    total_fees: Math.round(total_fees * 100) / 100,
    total_repayment: Math.round((p + total_fees) * 100) / 100,
  };
}

function FormField({ label, testId, children, className = '' }) {
  return (
    <div className={`space-y-1.5 ${className}`}>
      <Label className="text-xs font-medium text-slate-600 uppercase tracking-wide">{label}</Label>
      {children}
    </div>
  );
}

function FeeRow({ label, value, bold, accent }) {
  return (
    <div className={`flex justify-between ${bold ? 'font-semibold text-base' : ''}`}>
      <span className={accent ? 'text-emerald-300' : 'opacity-80'}>{label}</span>
      <span className={accent ? 'text-emerald-300' : ''}>R{value.toLocaleString('en-ZA', { minimumFractionDigits: 2 })}</span>
    </div>
  );
}

function SummaryRow({ label, value }) {
  return (
    <>
      <span className="text-slate-500">{label}</span>
      <span className="font-medium text-slate-800">{value || '—'}</span>
    </>
  );
}

function DocUpload({ label, fieldKey, fileName, fileId, uploading, onUpload, disabled }) {
  return (
    <div className="flex items-center justify-between p-4 bg-white border border-slate-200 rounded-lg" data-testid={`doc-${fieldKey}`}>
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${fileId ? 'bg-emerald-100' : 'bg-slate-100'}`}>
          {fileId ? <Check className="w-5 h-5 text-emerald-600" /> : <FileText className="w-5 h-5 text-slate-400" />}
        </div>
        <div>
          <p className="text-sm font-medium text-slate-800">{label}</p>
          <p className="text-xs text-slate-500">{fileName || 'JPG, PNG or PDF (max 10MB)'}</p>
        </div>
      </div>
      <label className={`cursor-pointer ${disabled ? 'opacity-50 pointer-events-none' : ''}`}>
        <input type="file" className="hidden" accept=".jpg,.jpeg,.png,.pdf" onChange={onUpload} disabled={disabled || uploading} data-testid={`upload-${fieldKey}-input`} />
        <span className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition ${
          fileId ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-[#0f2b4a] text-white hover:bg-[#1a3d5c]'
        }`} data-testid={`upload-${fieldKey}-btn`}>
          {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
          {fileId ? 'Replace' : 'Upload'}
        </span>
      </label>
    </div>
  );
}

function ConsentBox({ testId, checked, onCheckedChange, label }) {
  return (
    <div className="flex items-start gap-3 p-3 rounded-lg border border-slate-200 bg-white">
      <Checkbox checked={checked} onCheckedChange={onCheckedChange} data-testid={testId} className="mt-0.5" />
      <p className="text-sm text-slate-700 leading-relaxed">{label}</p>
    </div>
  );
}
